<template>
    <!-- 折叠模式 -->
    <div
        v-if="collapsible"
        ref="wrapperRef"
        v-pt-clickoutside:mousedown="handleClickOutside"
        :class="['collapsible-multi-select', { focus: isExpanded }]"
    >
        <!-- 折叠状态：显示 valueTitle -->
        <div
            v-show="!isExpanded"
            class="collapsible-multi-select__title"
            :class="[!hasValue && 'placeholder']"
            @click="handleExpand"
        >
            <div
                v-if="hasValue"
                class="collapsible-multi-select__value-title"
                :title="formatValueTitle"
                v-html="valueTitle"
            ></div>
            <span v-else class="collapsible-multi-select__placeholder">{{
                placeholder || $t('common.multiple_explain')
            }}</span>
            <span class="collapsible-multi-select__suffix">
                <i class="el-icon-arrow-down"></i>
            </span>
            <div v-if="visibleCount > 0" class="collapsible-multi-select__count">
                <p class="collapsible-multi-select__count-number">+ {{ visibleCount }}</p>
            </div>
        </div>

        <!-- 展开状态：显示 pt-multi-select -->
        <pt-multi-select
            v-show="isExpanded"
            ref="multiSelectRef"
            :size="size"
            :value="currValue"
            :value-key="valueKey"
            :options="options"
            :remote="remote"
            :allow-create="allowCreate"
            :placeholder="placeholder"
            :tag-placeholder="tagPlaceholder"
            :loading="remoteLoading"
            :remote-method="remoteMethod"
            :blur-create="true"
            :trim-space="needTrimSpace"
            :allow-empty="false"
            :expandAfterInit="false"
            @change="handleChange"
        ></pt-multi-select>
    </div>

    <!-- 普通模式 -->
    <pt-multi-select
        v-else
        :size="size"
        :value="currValue"
        :value-key="valueKey"
        :options="options"
        :remote="remote"
        :allow-create="allowCreate"
        :placeholder="placeholder"
        :tag-placeholder="tagPlaceholder"
        :loading="remoteLoading"
        :remote-method="remoteMethod"
        :blur-create="true"
        :trim-space="needTrimSpace"
        :allow-empty="false"
        :expandAfterInit="expandAfterInit"
        @change="handleChange"
    ></pt-multi-select>
</template>

<script>
export default {
    name: 'multipleItem',

    props: {
        size: String,
        value: {
            default: function () {
                return [];
            },
            required: true
        },
        valueKey: {
            type: String,
            default: 'code'
        },
        currFilter: Object,
        options: Array,
        placeholder: String,
        remote: Boolean,
        allowCreate: Boolean,
        remoteLoading: Boolean,
        expandAfterInit: {
            type: Boolean,
            default: false
        },
        collapsible: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            currValue: this.value || [],
            isExpanded: false,
            visibleCount: 0
        };
    },

    computed: {
        needTrimSpace() {
            return ['visitPage', 'entryPage', 'sourceUrl', 'sourceHost'].includes(
                this.currFilter?.type
            );
        },
        tagPlaceholder() {
            return this.currFilter && ['![()]', '!='].includes(this.currFilter.condition)
                ? this.$t('common.and_lowercase')
                : this.$t('common.or');
        },
        hasValue() {
            return Array.isArray(this.currValue) && this.currValue.length > 0;
        },
        // 计算分隔符
        computedSeparateWord() {
            return ['![()]', '!='].includes(this.currFilter?.condition)
                ? ` ${this.$t('common.and_lowercase')} `
                : ` ${this.$t('common.or')} `;
        },
        valueTitle() {
            if (!this.hasValue) return '';
            const self = this;
            const valueTitles = this.currValue.map(function (item) {
                const optionItem =
                    self.options &&
                    self.options.find(function (o) {
                        return o[self.valueKey] === item;
                    });
                const displayName = optionItem ? optionItem.name : item;
                return self.getConditionTitle(displayName);
            });
            return valueTitles.join(this.getConditionSepreateWord(this.computedSeparateWord));
        },
        formatValueTitle() {
            if (!this.currValue) return '';
            const self = this;
            return this.currValue
                .map(function (item) {
                    const optionItem =
                        self.options &&
                        self.options.find(function (o) {
                            return o[self.valueKey] === item;
                        });
                    return optionItem ? optionItem.name : item;
                })
                .join(',\n');
        }
    },

    watch: {
        value: {
            handler(val) {
                this.currValue = val || [];
                if (!this.isExpanded && this.collapsible) {
                    this.$nextTick(() => {
                        this.handleWord();
                    });
                }
            },
            deep: true
        },
        isExpanded(val) {
            if (!val && this.collapsible) {
                this.$nextTick(() => {
                    this.handleWord();
                });
            }
        }
    },

    mounted() {
        if (this.collapsible && !this.isExpanded) {
            this.$nextTick(() => {
                this.handleWord();
            });
        }
    },

    methods: {
        remoteMethod(query) {},

        handleChange(values) {
            const valueArray = values.map(item => item.code);
            this.currValue = valueArray;
            this.$emit('change', valueArray, values);
        },

        handleExpand() {
            this.isExpanded = true;
            this.$nextTick(() => {
                const multiSelect = this.$refs.multiSelectRef;
                if (multiSelect) {
                    // 重新计算 mainWidth，因为之前是 v-show 隐藏状态，宽度可能未正确计算
                    if (multiSelect.$refs.mainEl) {
                        const { width } = multiSelect.$refs.mainEl.getBoundingClientRect();
                        if (width) {
                            multiSelect.mainWidth = width - 10;
                        }
                    }

                    // 聚焦输入框（必须先聚焦，才能正确初始化）
                    if (multiSelect.$refs.input) {
                        multiSelect.$refs.input.focus();
                    }

                    // 延迟后显示下拉框，确保聚焦已生效
                    setTimeout(() => {
                        if (multiSelect && this.options && this.options.length > 0) {
                            multiSelect.showDropdown();
                        }
                    }, 50);
                }
            });
        },

        handleClickOutside() {
            if (this.collapsible && this.isExpanded) {
                const multiSelect = this.$refs.multiSelectRef;
                if (multiSelect) {
                    multiSelect.hideDropdown();
                }
                this.isExpanded = false;
            }
        },

        handleWord() {
            // 传入外层 title 容器
            const titleEl = this.$refs.wrapperRef?.querySelector(
                '.collapsible-multi-select__title'
            );
            if (titleEl) {
                const valueTitle = titleEl.querySelector('.collapsible-multi-select__value-title');
                if (valueTitle) {
                    this.resetDom(valueTitle);
                    this.$nextTick(() => {
                        setTimeout(() => {
                            // 传入外层容器和内层元素
                            this.visibleCount = this.getConditionCount(titleEl, valueTitle);
                        });
                    });
                }
            }
        },

        getConditionTitle(text) {
            // 不在生成时添加 ellipsisWord，而是在计算时动态添加
            return '<span class="conditionKeyWord">' + this.escapeHtml(text) + '</span>';
        },

        getConditionSepreateWord(word) {
            return '<span class="conditionSepreateWord">' + this.escapeHtml(word) + '</span>';
        },

        escapeHtml(text) {
            const map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            };
            return String(text).replace(/[&<>"']/g, function (m) {
                return map[m];
            });
        },

        resetDom(el) {
            if (!el) return;

            const spans = el.querySelectorAll('span.conditionKeyWord');
            if (!spans || Array.from(spans).length === 0) return;

            // 重置所有元素：移除 ellipsisWord 类，清空内联 display 样式
            Array.from(spans).forEach(function (element) {
                element.classList.remove('ellipsisWord');
                element.style.display = ''; // 清空内联样式
            });

            // 重置所有分隔符
            const separators = el.querySelectorAll('span.conditionSepreateWord');
            Array.from(separators).forEach(function (separator) {
                separator.style.display = ''; // 清空内联样式
            });
        },

        getConditionCount(containerEl, valueEl) {
            if (!containerEl || !valueEl) return 0;

            const spans = valueEl.querySelectorAll('span.conditionKeyWord');
            if (!spans || Array.from(spans).length === 0) return 0;

            // 获取外层容器的宽度（.collapsible-multi-select__title）
            const { width: containerWidth } = containerEl.getBoundingClientRect();

            // 减去外层容器的 padding 和边框
            // padding: 4px 24px 4px 6px = 左右 30px, border: 1px * 2 = 2px
            const baseWidth = containerWidth - 32;

            // 获取分隔符的宽度
            const sepreateWordEl = valueEl.querySelector('span.conditionSepreateWord');
            const sepreateWord = sepreateWordEl ? sepreateWordEl.getBoundingClientRect().width : 0;

            let count = 0;
            let total = 0;

            // 预留空间：右侧箭头(24px) + count 数字区域(约 40px)
            const reservedSpace = 64;
            let hasEllipsis = false; // 标记是否已经添加了省略号

            Array.from(spans).forEach((element, index) => {
                const { width } = element.getBoundingClientRect();
                total = total + width + sepreateWord + 4;

                if (total - sepreateWord > baseWidth - reservedSpace) {
                    if (!hasEllipsis) {
                        // 第一个超出的元素：添加省略号，仍然可见
                        element.classList.add('ellipsisWord');
                        hasEllipsis = true;
                    } else {
                        // 后续超出的元素：直接隐藏
                        element.style.display = 'none';
                        // 隐藏当前元素前面的分隔符
                        if (element.previousSibling) {
                            element.previousSibling.style.display = 'none';
                        }
                    }
                }
            });

            // 循环结束后，统计所有被隐藏的元素（display: none）
            // 只统计完全不可见的元素，不包括显示省略号的元素
            count = 0;
            Array.from(spans).forEach(element => {
                if (element.style.display === 'none') {
                    count++;
                }
            });

            return count;
        }
    }
};
</script>

<style lang="scss">
.collapsible-multi-select {
    position: relative;
    width: 100%;
    min-width: 0; // 防止 flex 子元素溢出

    &__title {
        display: flex;
        align-items: center;
        position: relative;
        // 与 multi-select__wrap 保持完全一致
        // multi-select: border 1px + padding-top 2px + tag(24px + margin 2px) + padding-bottom 2px + border 1px
        padding: 4px 24px 4px 6px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
        transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
        overflow: hidden; // 防止内容溢出
        box-sizing: border-box;
        font-size: 13px;

        &:hover {
            border-color: #c0c4cc;
        }

        &.focus {
            border-color: #42b983;
        }

        &.placeholder {
            color: #8993a4;
        }
    }

    &__value-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: flex;
        align-items: center;
        height: 24px;
        flex: 1;
        min-width: 0; // 防止 flex 子元素溢出
        padding-right: 32px; // 为 count 预留空间
    }

    &__placeholder {
        display: inline-block;
        line-height: 22px;
        font-size: 13px;
        color: #8993a4;
    }

    &__suffix {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        color: #8993a4;
    }

    &__count {
        position: absolute;
        right: 24px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        background-color: #fff; // 添加背景色防止文字透过
        padding-left: 4px;

        &-number {
            line-height: 28px;
            font-size: 12px;
            color: #5b6c86;
        }
    }

    // 编辑状态下的 multi-select 样式修复
    .multi-select {
        width: 100%;

        &__wrap {
            width: 100%;
        }

        &__list {
            .inner {
                .tagBase {
                    max-width: none !important;
                    flex-shrink: 0;
                }

                .tag {
                    // 确保 tag 使用内容宽度，移除 maxWidth 的内联样式限制
                    max-width: none !important;
                    width: auto !important;
                    flex-shrink: 0;

                    span {
                        width: auto !important;
                        max-width: none !important;
                    }
                }

                .tag-input {
                    flex: 1;
                    min-width: 60px;
                }
            }
        }
    }
}

// 全局样式，用于 valueTitle 内的关键词样式（与 UsergroupFilterCondition 保持一致）
.conditionKeyWord {
    box-sizing: border-box;
    border-color: transparent;
    margin: 0 2px;
    background-color: #f2f7f2;
    height: 24px;
    padding: 0 8px;
    line-height: 24px;
    white-space: nowrap;
    border-radius: 4px;
    font-size: 13px;
    color: #091e42;
    max-width: 100%;
    display: block; // 与 UsergroupFilterCondition 保持一致
}

.ellipsisWord {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.conditionSepreateWord {
    margin: 0 2px;
    color: #8993a4;
    font-size: 13px;
}
</style>
